import {
  emailDoesnTExistMessageDisplayed,
  loginSignupPageClickOnSwitchForm,
  loginSubmit,
  onSwitchContext,
} from '@wix/bi-logger-hls2/v2';
import { action, computed, makeObservable, observable } from 'mobx';
import React from 'react';
import { ROUTES } from '../routes';
import {
  AUTH_METHODS_BY_MAIL,
  CaptchaStatus,
  EXPERIMENTS,
  FORGOT_PASSWORD_LINK,
  GET_USER_ACCOUNTS_CONTEXT,
  LOGIN_PAGE_CONTEXT,
  SIGNUP_PAGE_CONTEXT,
  TWOFA_PROCESS,
} from '../utils/constants';
import {
  ERROR_CODES,
  extractErrorKeyByErrorCode,
  extractRedirectUrl,
} from '../utils/errorHandler';
import { createTextWithLink } from '../utils/generator';
import {
  Constraint,
  constraints,
  ERROR_TRANSLATION_KEYS,
  generateEmailFieldConstraints,
} from '../utils/validators';
import { CAPTCHA_ACTIONS } from './captcha';
import { FormField } from './formField';
import { RootStore } from './root';
import { SIGNUP_FLOWS } from './signup';
import { HttpError } from '@wix/http-client';
import { Values, WebAuthnAuthenticationData } from '../types';
import { SHOULD_BLOCK_EDITOR_X_SIGNUP_FT } from '../server/constants';
import { PasswordFormField } from './passwordFormField';
import { AUTH_TYPE } from '../components/BlockedAccount/authTypes';
import { StateType } from '@wix/ambassador-iam-authentication-v1-authentication/types';
import { PRESETS } from '../utils/presets';
import { createBiLogger } from '../services/biLogger';
import { AuthMethod, setLastUsedAuthMethod } from '../utils/lastUsedAuthMethod';
import {
  lastUsedBiView,
  lastUsedTransactionData,
} from '../utils/lastUsedAuthMethodBi';

const MAP_ERROR_CODE_TO_FIELD_TYPE = {
  [ERROR_CODES.PASSWORD_INCORRECT]: 'password',
  [ERROR_CODES.IAM_PASSWORD_INCORRECT]: 'password',
  [ERROR_CODES.INVALID_EMAIL_OR_PASSWORD]: 'password',
  [ERROR_CODES.USER_DELETED_OR_BLOCKED]: 'email',
  [ERROR_CODES.USER_IS_DELETED]: 'email',
  [ERROR_CODES.USER_IS_BLOCKED]: 'email',
  [ERROR_CODES.LOGIN_NO_SUCH_MAIL]: 'email',
  [ERROR_CODES.LOGIN_NO_SUCH_MAIL_IAM]: 'email',
  [ERROR_CODES.SOCIAL_LOGIN_GET_EMAIL]: 'email',
  [ERROR_CODES.GENERAL_ERROR_CODE]: 'email',
  [ERROR_CODES.RESET_PASSWORD_REQUIRED]: 'email',
  [ERROR_CODES.RESET_PASSWORD_REQUIRED_IAM]: 'email',
  [ERROR_CODES.FACTOR_REQUIRES_RE_ENROLLMENT]: 'email',
  [ERROR_CODES.SSO_LOGIN_MANDATORY_ERROR]: 'email',
  [ERROR_CODES.FACTOR_NOT_FOUND]: 'email',
  [ERROR_CODES.INACTIVE_FACTOR]: 'email',
};

export const LOGIN_FLOWS = {
  FROM_SIGNUP: 'FROM_SIGNUP',
  DEFAULT: 'DEFAULT',
};
export type ILoginFlows = Values<typeof LOGIN_FLOWS>;

export class LoginStore implements AuthStore {
  private readonly rootStore: RootStore;
  private captchaAdded: CaptchaStatus = {
    [LOGIN_PAGE_CONTEXT]: false,
    [SIGNUP_PAGE_CONTEXT]: false,
    [GET_USER_ACCOUNTS_CONTEXT]: false,
  };
  public emailField: FormField;
  public isLoading: boolean = false;
  public passwordField: PasswordFormField;
  private LOGIN_INTERACTION_NAME = 'email-login';
  private loginActionErrorMessage?: string;
  public flow: string = LOGIN_FLOWS.DEFAULT;
  public stateToken?: string;
  private stateTokenEmail?: string;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    const { i18n } = this.rootStore;
    const emailRules: Constraint[] = generateEmailFieldConstraints(
      i18n.t.bind(i18n),
    );
    this.emailField = new FormField(
      this.rootStore.userDataStore.email,
      emailRules,
    );
    const fieldRequiredConstraint: Constraint[] = [
      [constraints.required, i18n.t(ERROR_TRANSLATION_KEYS.REQUIRED)],
    ];
    this.passwordField = new PasswordFormField('', fieldRequiredConstraint);

    makeObservable(this, {
      emailField: observable,
      passwordField: observable,
      isLoading: observable,
      titleKey: computed,
      subtitleKey: computed,
      presetSubtitleKey: computed,
      login: action.bound,
      onInitFields: action.bound,
      clear: action.bound,
      onClickForgotPassword: action.bound,
    });
  }

  get shouldDisplayOneTap() {
    return (
      !this.rootStore.displayStore.isMobile &&
      this.rootStore.displayStore.viewMode !== 'MODAL' &&
      !this.rootStore.displayStore.presetKey &&
      this.rootStore.experiments.enabled(
        EXPERIMENTS.SHOULD_DISPLAY_GOT_ON_SIGNUP_PAGE,
      )
    );
  }

  onNavigateToLogin(flow = LOGIN_FLOWS.DEFAULT) {
    this.flow = flow;
    this.onInitFields();
    this.rootStore.navigationStore.navigate(
      ROUTES.LOGIN_PASSWORD,
      this.flow === LOGIN_FLOWS.FROM_SIGNUP ? 'Login - from signup' : undefined,
    );
  }

  clear(): void {
    this.emailField.clear();
    this.passwordField.clear();
    this.clearStateToken();
    this.captchaAdded = {
      [LOGIN_PAGE_CONTEXT]: false,
      [SIGNUP_PAGE_CONTEXT]: false,
      [GET_USER_ACCOUNTS_CONTEXT]: false,
    };
  }

  clearEmail(): void {
    this.clear();
    // Going back to the email step means there's no longer a resolved account,
    // so drop its cached social connections. Otherwise the Symphony Facebook
    // gate (socialAuth.showFacebook) would keep reading the previously
    // looked-up account and leave Facebook visible on the email step, unlike a
    // fresh landing.
    this.rootStore.socialAuthStore.setAccountSocialConnectionsProviders([]);
    this.rootStore.navigationStore.navigate(ROUTES.EMAIL_STEP);
  }

  onInitFields(): void {
    this.passwordField.clear();
    this.captchaAdded = {
      [LOGIN_PAGE_CONTEXT]: false,
      [SIGNUP_PAGE_CONTEXT]: false,
      [GET_USER_ACCOUNTS_CONTEXT]: false,
    };
    const { email } = this.rootStore.userDataStore;
    email && this.emailField.setValue(email);
  }

  onClickForgotPassword() {
    this.rootStore.biLogger.report(
      onSwitchContext({
        context: 'forgot-password',
        referrer: this.rootStore.navigationStore.originContext,
      }),
    );
  }

  async handleNoSuchEmailError() {
    const { userDataStore, signupStore, biLogger } = this.rootStore;
    biLogger.report(
      emailDoesnTExistMessageDisplayed({ context: 'email-login' }),
    );
    userDataStore.setEmail(this.emailField.value);
    try {
      // Stays loading until the signup navigation resolves - it can await a
      // verdict first, and `isSubmittable` is what blocks a second submit.
      await signupStore.onNavigateToSignup(SIGNUP_FLOWS.FROM_LOGIN);
    } finally {
      this.isLoading = false;
    }
  }

  get titleKey() {
    const { login } = this.rootStore.displayStore.preset;
    if (this.flow === LOGIN_FLOWS.FROM_SIGNUP) {
      return login?.alreadyHaveAccount?.title?.key ?? 'login.from_signup.title';
    }
    return login?.title?.key ?? 'login.customized.title';
  }

  get subtitleKey() {
    if (
      this.flow === LOGIN_FLOWS.FROM_SIGNUP &&
      this.rootStore.displayStore.preset.id !== PRESETS.WIXEL
    ) {
      return 'login.from_customized_signup.description';
    }
    return undefined;
  }

  get priority() {
    if (
      this.flow === LOGIN_FLOWS.FROM_SIGNUP &&
      this.rootStore.displayStore.preset.id !== PRESETS.WIXEL
    ) {
      return 'secondary';
    }
    return 'primary';
  }

  get presetSubtitleKey() {
    if (this.flow === LOGIN_FLOWS.FROM_SIGNUP) {
      return (
        this.rootStore.displayStore.preset?.login?.alreadyHaveAccount?.subtitle
          ?.key ?? this.rootStore.displayStore.preset?.login?.subtitle?.key
      );
    }
    return this.rootStore.displayStore.preset?.login?.subtitle?.key;
  }

  public setStateToken(stateToken?: string, email?: string) {
    this.stateToken = stateToken;
    this.stateTokenEmail = email;
  }

  private clearStateToken() {
    this.stateToken = undefined;
    this.stateTokenEmail = undefined;
  }

  async executeLogin(
    email: string,
    password: string,
    recaptchaParams,
    webAuthnAuthenticationData?: WebAuthnAuthenticationData,
  ) {
    this.loginActionErrorMessage = undefined;
    // The email path is never badged, but it is half the comparison: these
    // are the people a stale social badge would have pulled away. Attributing
    // it is what makes "did the badge cost the email form anything"
    // answerable.
    const lastUsedData = lastUsedTransactionData(
      this.rootStore.experiments,
      AuthMethod.EMAIL,
    );
    this.rootStore.panorama.startTransaction(
      this.LOGIN_INTERACTION_NAME,
      lastUsedData,
    );
    const stateToken =
      this.stateTokenEmail === email ? this.stateToken : undefined;
    try {
      const data = await this.rootStore.apiStore.login(
        this.rootStore.apiStore.applyAdditionalLoginParams({
          email,
          password,
          rememberMe: true,
          ...recaptchaParams,
          webAuthnAuthenticationData,
          stateToken,
        }),
      );
      this.clearStateToken();

      this.rootStore.panorama.finishTransaction(
        this.LOGIN_INTERACTION_NAME,
        lastUsedData,
      );
      this.rootStore.tagManagerStore.log('editorx.first.login', {
        email,
      });
      this.rootStore.biLogger = createBiLogger();
      this.rootStore.postLoginStore.biLogger = createBiLogger();
      // Email is never badged (the form is already the default path) — the
      // point of recording it is to retire a social badge the user has since
      // moved away from.
      setLastUsedAuthMethod(AuthMethod.EMAIL);
      return this.rootStore.navigationStore.postLogin(data?.payload ?? {});
    } catch (error: any) {
      const errorCode =
        error.errorCode?.toString() ?? ERROR_CODES.GENERAL_ERROR_CODE;
      this.loginActionErrorMessage = error.message || String(error);
      if (errorCode !== ERROR_CODES.GENERAL_ERROR_CODE) {
        this.rootStore.panorama.finishTransaction(
          this.LOGIN_INTERACTION_NAME,
          lastUsedData,
        );
      }
      if (errorCode === ERROR_CODES.SECOND_FACTOR_REQUIRED && error.payload) {
        this.rootStore.panorama.startTransaction(TWOFA_PROCESS);
        if (error.payload?.state === StateType.MFA_CHALLENGE) {
          this.rootStore.twoFactorAuthStore.setAuthParamsMfaIAM(error.payload);
        } else {
          this.rootStore.twoFactorAuthStore.setAuthParams(error.payload);
        }
        this.rootStore.navigationStore.navigate(ROUTES.TWO_FACTOR_AUTH);
        this.isLoading = false;
        return;
      }
      if (
        errorCode === ERROR_CODES.LOGIN_NO_SUCH_MAIL ||
        errorCode === ERROR_CODES.LOGIN_NO_SUCH_MAIL_IAM
      ) {
        return this.handleNoSuchEmailError();
      }
      if (
        errorCode === ERROR_CODES.SSO_LOGIN_MANDATORY_ERROR ||
        errorCode === ERROR_CODES.FACTOR_NOT_FOUND ||
        errorCode === ERROR_CODES.INACTIVE_FACTOR
      ) {
        const redirectUrl = extractRedirectUrl(error);
        if (redirectUrl) {
          return this.rootStore.navigationStore.redirect(redirectUrl);
        }
      }
      // The user has no password, but he does have an account (or else we would get 'LOGIN_NO_SUCH_MAIL' error),
      // so we fetch more data to know what should be the correct next step
      if (this.shouldFetchAllAccountAfterFailedLogin(errorCode)) {
        throw errorCode;
      }
      this.isLoading = false;
      if (errorCode === ERROR_CODES.USER_IS_BLOCKED) {
        return this.rootStore.navigationStore.navigateToBlockedAccount({
          refferal_info: AUTH_TYPE.EMAIL_AND_PASSWORD,
        });
      }
      throw errorCode;
    }
  }

  async login(
    email?: string,
    webAuthnAuthenticationData?: WebAuthnAuthenticationData,
  ) {
    if (email !== undefined) {
      this.emailField.setValue(email);
    }

    this.rootStore.biLogger.report(
      loginSubmit({
        remember_me: true,
        view: lastUsedBiView(this.rootStore.experiments, AuthMethod.EMAIL),
      }),
    );
    if (!this.isSubmittable(webAuthnAuthenticationData)) {
      this.markFormAsDirty();
      return;
    }
    this.isLoading = true;
    const recaptchaParams =
      await this.rootStore.captchaStore.handleRecaptchaExecution({
        captchaAdded: this.captchaAdded[LOGIN_PAGE_CONTEXT],
        action: CAPTCHA_ACTIONS.LOGIN,
      });
    this.executeLogin(
      this.emailField.value,
      this.passwordField.value,
      recaptchaParams,
      webAuthnAuthenticationData,
    )
      .catch((errorCode) => {
        // Webauthn api error handling
        if (webAuthnAuthenticationData) {
          this.rootStore.webauthnStore.disablePasskeyFlow();
          const passkeyErrorCode =
            errorCode === ERROR_CODES.WEBAUTHN_ASSERTION_FAILED
              ? ERROR_CODES.WEBAUTHN_ASSERTION_FAILED
              : ERROR_CODES.TECHNICAL_ERROR;
          this.rootStore.webauthnStore.reportPasskeyError(
            passkeyErrorCode,
            this.loginActionErrorMessage,
          );
          this.rootStore.notificationStore.openErrorToast(
            this.rootStore.i18n.t(extractErrorKeyByErrorCode(passkeyErrorCode)),
          );
          return;
        }

        if (this.shouldFetchAllAccountAfterFailedLogin(errorCode)) {
          return this.handleUserWithFailedLogin(errorCode);
        }
        if (!this.rootStore.captchaStore.isCaptchaServerError(errorCode)) {
          this.rootStore.modalModeHandlerStore.handleErrorReport({
            errorCode,
          });
          this.addErrorToField(errorCode);
          if (
            errorCode === ERROR_CODES.RESET_PASSWORD_REQUIRED ||
            errorCode === ERROR_CODES.RESET_PASSWORD_REQUIRED_IAM ||
            errorCode === ERROR_CODES.FACTOR_REQUIRES_RE_ENROLLMENT
          ) {
            this.emailField.isWarning = true;
          }
        }
        this.captchaAdded[LOGIN_PAGE_CONTEXT] =
          this.rootStore.captchaStore.createOrResetCaptchaIfNeeded(
            errorCode,
            this.captchaAdded[LOGIN_PAGE_CONTEXT],
          );
      })
      .catch((error) => {
        this.rootStore.modalModeHandlerStore.handleErrorReport(error);
      });
  }

  shouldFetchAllAccountAfterFailedLogin(errorCode: string) {
    const errors = [
      ERROR_CODES.SSO_LOGIN_MANDATORY_ERROR,
      ERROR_CODES.INVALID_EMAIL_OR_PASSWORD,
      ERROR_CODES.PASSWORD_INCORRECT,
      ERROR_CODES.IAM_PASSWORD_INCORRECT,
      ERROR_CODES.FACTOR_NOT_FOUND,
      ERROR_CODES.INACTIVE_FACTOR,
    ];
    return errors.some((e) => e === errorCode);
  }

  async generalHandleUserWithNoPassword({
    captchaAdded,
    email,
    authFlowAdditionalParmas = {},
    originAuthMethod,
    originAuthError,
  }: {
    captchaAdded: boolean;
    email: string;
    authFlowAdditionalParmas?: { [key: string]: any };
    originAuthMethod?: string;
    originAuthError?: string;
  }) {
    const { captchaStore, apiStore, emailStepStore } = this.rootStore;
    const recaptchaParams = await captchaStore.handleRecaptchaExecution({
      captchaAdded,
      action: CAPTCHA_ACTIONS.GET_USER_ACCOUNTS,
    });

    try {
      const response = await apiStore.getUserAccounts({
        email,
        ...recaptchaParams,
      });

      const authType = emailStepStore.getAuthMethodByAccountData(
        response?.accountsData!,
        email,
      );

      if (authType.method === originAuthMethod) {
        return { success: false, errorCode: originAuthError };
      }
      emailStepStore.authHandlers[authType.method]({
        email,
        ...(authType.params ?? {}),
        ...authFlowAdditionalParmas,
      } as any);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        errorCode: (error as HttpError)?.response?.data.errorCode?.toString(),
      };
    }
  }

  private async handleUserWithFailedLogin(loginErrorCode: string) {
    this.rootStore.userDataStore.setEmail(this.emailField.value);
    const { success, errorCode } = await this.generalHandleUserWithNoPassword({
      captchaAdded: this.captchaAdded[GET_USER_ACCOUNTS_CONTEXT],
      email: this.emailField.value,
      originAuthMethod: AUTH_METHODS_BY_MAIL.LOGIN_PASSWORD,
      originAuthError: loginErrorCode,
    });
    this.isLoading = false;
    if (!success) {
      // A visible-captcha escalation (9981/9982) is not a failure to surface as
      // a field error — it's handled by rendering the captcha below. Showing the
      // message here mapped to the generic "general technical error" (no key for
      // the captcha codes), hiding the fact that the user just needs to solve the
      // "I'm not a robot" challenge.
      if (!this.rootStore.captchaStore.isCaptchaServerError(errorCode)) {
        this.addErrorToField(errorCode);
      }
      this.captchaAdded[GET_USER_ACCOUNTS_CONTEXT] =
        this.rootStore.captchaStore.createOrResetCaptchaIfNeeded(
          errorCode,
          this.captchaAdded[GET_USER_ACCOUNTS_CONTEXT],
        );
    }
  }

  public shouldBlockEditorXSignup() {
    const shouldBlockEditorXSignup = this.rootStore.experiments.enabled(
      SHOULD_BLOCK_EDITOR_X_SIGNUP_FT,
    );
    return shouldBlockEditorXSignup && this.rootStore.displayStore.isEditorX;
  }

  private addErrorToField(errorCode: string) {
    const fields = {
      password: this.passwordField,
      email: this.emailField,
    };
    const errorElement = this.generateErrorElement(errorCode);
    const fieldWithError =
      MAP_ERROR_CODE_TO_FIELD_TYPE[errorCode] || 'password';
    fields[fieldWithError]?.addError(errorElement);
  }

  private generateErrorElement(errorCode: string): string | React.ReactElement {
    const errorKey = extractErrorKeyByErrorCode(errorCode, {
      context: 'password',
    });
    if (
      errorCode === ERROR_CODES.RESET_PASSWORD_REQUIRED ||
      errorCode === ERROR_CODES.RESET_PASSWORD_REQUIRED_IAM ||
      errorCode === ERROR_CODES.FACTOR_REQUIRES_RE_ENROLLMENT
    ) {
      const sessionId = this.rootStore.sessionId.get();
      return createTextWithLink(
        errorKey,
        FORGOT_PASSWORD_LINK(
          sessionId,
          this.rootStore.navigationStore.overrideLocale,
        ),
      );
    }
    return this.rootStore.i18n.t(errorKey);
  }

  public isSubmittable(
    webAuthnAuthenticationData?: WebAuthnAuthenticationData,
  ): boolean {
    if (this.isLoading) {
      return false;
    }
    const hasWebAuthn = !!webAuthnAuthenticationData;
    const hasValidEmailAndPassword =
      this.emailField.isValid && this.passwordField.isValid;
    return hasWebAuthn || hasValidEmailAndPassword;
  }

  private markFormAsDirty() {
    this.emailField.markFieldAsDirty();
    this.passwordField.markFieldAsDirty();
  }

  public reportSwitchToSignup() {
    this.rootStore.biLogger.report(
      loginSignupPageClickOnSwitchForm({
        switch_to: 'signup',
        context: 'login_second_step',
      }),
    );
  }
}
